import { useEffect } from 'react';

/**
 * Sets `<html lang>` while a brand page is mounted and puts the previous value back once the last
 * page that asked for it is gone.
 *
 * One SPA build serves every brand and public/index.html declares lang="da", so without this the
 * Swedish and Finnish pages were marked Danish for visitors, screen readers and any tool that reads
 * the shell (the SEO review of 2026-11-04 found it; bots get the prerendered snapshots, which carry
 * the right language on their own). Each brand's Common/Header calls it with the brand's language.
 *
 * The product pages mount a brand Header twice (desktop + mobile wrappers), so the holders are
 * counted: the first one sets the language, the last one to leave restores what was there before.
 */
const holders = new Map();   // lang → { count, previous }

export default function useDocumentLang(lang) {
  useEffect(() => {
    if (!lang) return undefined;
    const root = document.documentElement;
    let h = holders.get(lang);
    if (!h) {
      h = { count: 0, previous: root.getAttribute('lang') };
      holders.set(lang, h);
      root.setAttribute('lang', lang);
    }
    h.count++;
    return () => {
      h.count--;
      if (h.count > 0) return;
      holders.delete(lang);
      if (root.getAttribute('lang') !== lang) return;   // someone else changed it since — leave it
      if (h.previous) root.setAttribute('lang', h.previous);
      else root.removeAttribute('lang');
    };
  }, [lang]);
}
